import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { CaptainLogo } from "./CaptainLogo";
import { useIsMobile } from "@/hooks/use-mobile";

interface NavbarProps {
  lang: string;
  setLang: (lang: string) => void;
  onOpenChat?: () => void;
  t: (en: string, fr: string, es?: string) => string;
}

/**
 * FISL.fr-inspired structure: dark horizontal bar, grouped dropdowns,
 * highlighted CTA. Captain ROCA palette via semantic tokens.
 */

type MenuItem = { href: string; en: string; fr: string; es: string };
type MenuGroup = {
  key: string;
  en: string;
  fr: string;
  es: string;
  items: MenuItem[];
};

const GROUPS: MenuGroup[] = [
  {
    key: "formations",
    en: "Training",
    fr: "Formations",
    es: "Formaciones",
    items: [
      { href: "/permis-academy", en: "Permis Côtier", fr: "Permis Côtier", es: "Permiso Costero" },
      { href: "/permis-academy#fluvial", en: "Permis Fluvial", fr: "Permis Fluvial", es: "Permiso Fluvial" },
      { href: "/crr-zone1", en: "CRR (VHF)", fr: "CRR (VHF)", es: "CRR (VHF)" },
      { href: "/bpjeps-motonautisme", en: "BPJEPS Motonautisme", fr: "BPJEPS Motonautisme", es: "BPJEPS Motonáutica" },
      { href: "/scuba-diving", en: "Scuba Diving", fr: "Plongée", es: "Buceo" },
    ],
  },
  {
    key: "centres",
    en: "Pathways",
    fr: "Nos parcours",
    es: "Nuestras rutas",
    items: [
      { href: "/maritime-careers", en: "Maritime Careers", fr: "Carrières maritimes", es: "Carreras marítimas" },
      { href: "/guadeloupe", en: "DROM-COM Map", fr: "Carte DROM-COM", es: "Mapa DROM-COM" },
      { href: "/jetski-ops-planner", en: "Captain Ops", fr: "Captain Ops", es: "Captain Ops" },
    ],
  },
  {
    key: "equipe",
    en: "Crew",
    fr: "Équipe",
    es: "Equipo",
    items: [
      { href: "/dt-profiles", en: "Ocean Navigators", fr: "Profils DT", es: "Perfiles DT" },
      { href: "/about", en: "About", fr: "À propos", es: "Acerca" },
    ],
  },
  {
    key: "elearning",
    en: "E-learning",
    fr: "E-learning",
    es: "E-learning",
    items: [
      { href: "/crr-flash", en: "CRR Flash", fr: "CRR Flash", es: "CRR Flash" },
      { href: "/crr-zone1", en: "Zone 1", fr: "Zone 1", es: "Zona 1" },
      { href: "/crr-zone2", en: "Zone 2", fr: "Zone 2", es: "Zona 2" },
      { href: "/crr-zone3", en: "Zone 3", fr: "Zone 3", es: "Zona 3" },
      { href: "/crr-zone4", en: "Zone 4", fr: "Zone 4", es: "Zona 4" },
    ],
  },
  {
    key: "infos",
    en: "Practical info",
    fr: "Infos pratiques",
    es: "Info práctica",
    items: [
      { href: "/contact", en: "Contact", fr: "Contact", es: "Contacto" },
      { href: "/install", en: "Install app", fr: "Installer l'app", es: "Instalar app" },
    ],
  },
  {
    key: "pro",
    en: "Pro",
    fr: "Pages pro",
    es: "Pro",
    items: [
      { href: "/maritime-careers", en: "Recruiters", fr: "Recruteurs", es: "Reclutadores" },
      { href: "/dt-profiles", en: "Talent pool", fr: "Vivier talents", es: "Banco de talentos" },
    ],
  },
];

export const Navbar = ({ lang, setLang, t }: NavbarProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [openMobileGroup, setOpenMobileGroup] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border/40 shadow-lg">
      {/* Top utility bar */}
      {!isMobile && (
        <div className="border-b border-border/20">
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-8">
            <span className="text-[10px] text-muted-foreground tracking-wide">
              {t("AI Maritime Companion", "Compagnon Maritime IA", "Compañero Marítimo IA")}
            </span>
            <div className="flex items-center gap-1">
              <Globe className="w-3 h-3 text-muted-foreground mr-1" />
              {(["fr", "en", "es"] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                    lang === l
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main navbar */}
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 no-underline shrink-0">
          <CaptainLogo size={32} />
          <div className="leading-tight">
            <span className="font-bold text-sm tracking-wide text-foreground">
              Captain ROCA
            </span>
            {!isMobile && (
              <span className="block text-[10px] text-muted-foreground">
                AI Companion
              </span>
            )}
          </div>
        </Link>

        {/* Desktop nav — grouped dropdowns */}
        {!isMobile && (
          <div className="flex items-center h-full">
            {GROUPS.map((group) => (
              <div
                key={group.key}
                className="relative h-full flex items-center"
                onMouseEnter={() => setOpenGroup(group.key)}
                onMouseLeave={() => setOpenGroup(null)}
              >
                <button
                  className={`h-full px-4 flex items-center gap-1 text-sm font-medium transition-colors ${
                    openGroup === group.key
                      ? "text-primary bg-secondary/40"
                      : "text-foreground/85 hover:text-primary"
                  }`}
                >
                  {t(group.en, group.fr, group.es)}
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </button>

                {openGroup === group.key && (
                  <div className="absolute top-full left-0 min-w-[240px] bg-card border border-border/40 rounded-b-lg shadow-xl py-2 animate-in fade-in slide-in-from-top-1 duration-150">
                    {group.items.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="block px-4 py-2.5 text-sm text-foreground/80 hover:text-primary hover:bg-secondary/50 transition-colors no-underline"
                      >
                        {t(item.en, item.fr, item.es)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Highlighted CTA — FISL-style */}
            <Link
              to="/crr-flash"
              className="ml-2 h-full px-6 flex items-center text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors no-underline"
            >
              {t("Start now", "Démarrer", "Empezar")}
            </Link>
          </div>
        )}

        {/* Mobile right side */}
        <div className="flex items-center gap-2 md:hidden">
          {isMobile && (
            <div className="flex items-center gap-0.5 mr-1">
              {(["fr", "en", "es"] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-1.5 py-1 rounded text-[10px] font-medium transition-colors ${
                    lang === l
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          )}
          {isMobile && (
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-foreground hover:bg-secondary transition-colors"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu — accordion groups */}
      {isMobile && mobileOpen && (
        <div className="bg-card/98 backdrop-blur-lg border-t border-border/30 animate-in slide-in-from-top-2 duration-200 max-h-[80vh] overflow-y-auto">
          <div className="px-3 py-3 space-y-1">
            {GROUPS.map((group) => {
              const open = openMobileGroup === group.key;
              return (
                <div key={group.key} className="border-b border-border/20 last:border-0">
                  <button
                    onClick={() => setOpenMobileGroup(open ? null : group.key)}
                    className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground"
                  >
                    {t(group.en, group.fr, group.es)}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
                    />
                  </button>
                  {open && (
                    <div className="pb-2 pl-2">
                      {group.items.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          className="block px-4 py-2.5 rounded-lg text-sm text-foreground/80 hover:bg-secondary/50 no-underline"
                        >
                          {t(item.en, item.fr, item.es)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <Link
              to="/crr-flash"
              onClick={() => setMobileOpen(false)}
              className="block mt-3 mx-1 text-center px-6 py-3 rounded-lg text-sm font-semibold bg-primary text-primary-foreground no-underline"
            >
              {t("Start now", "Démarrer", "Empezar")}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};
